import { Combobox } from '@ibsys/kit';
import type { ComboboxItem } from '@ibsys/kit';
import { FC, useCallback, useMemo } from 'react';

/**
 * Значение пункта «Все» в выпадающем списке-донастройке. Ограничение не накладывается —
 * поиск идёт по всем (пустой набор конкретных значений).
 */
const ALL_VALUE = 'all';

/** Пропсы мультиселекта «Все + конкретные значения» */
export type AllOrSpecificMultiSelectProps = {
  /** Конкретные пункты (без «Все»); значение каждого — числовой id */
  options: ComboboxItem[];
  /** Выбранные id; пустой массив — «Все» */
  value: number[];
  /** Смена набора — управляющее действие уровня запроса (список перечитывается) */
  onChange: (ids: number[]) => void;
  /** Подпись пункта «Все» */
  allLabel: string;
  /** aria-label списка */
  ariaLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
  isLoading?: boolean;
  className?: string;
};

/**
 * Выпадающий список-донастройка с множественным выбором и первым пунктом «Все».
 * «Все» и конкретные значения взаимоисключающи: выбор «Все» снимает конкретные,
 * отметка первого конкретного снимает «Все», снятие всех конкретных возвращает к «Все».
 */
export const AllOrSpecificMultiSelect: FC<AllOrSpecificMultiSelectProps> = ({
  options,
  value,
  onChange,
  allLabel,
  ariaLabel,
  searchPlaceholder,
  emptyMessage,
  isLoading,
  className,
}) => {
  const optionsWithAll = useMemo<ComboboxItem[]>(
    () => [{ value: ALL_VALUE, label: allLabel }, ...options],
    [allLabel, options],
  );

  // «Все» отображается выбранным, пока нет конкретных значений.
  const selectedValues = useMemo<(string | number)[]>(
    () => (value.length > 0 ? value : [ALL_VALUE]),
    [value],
  );

  const handleChange = useCallback(
    (next: (string | number)[]) => {
      const isAllPicked = next.includes(ALL_VALUE);
      const ids = next.filter((item): item is number => typeof item === 'number');
      // Выбор «Все» поверх конкретных значений — сброс к «Все» (пустой набор).
      if (isAllPicked && value.length > 0) {
        onChange([]);
        return;
      }
      // Иначе действуют конкретные значения; их отсутствие тоже трактуется как «Все».
      onChange(ids);
    },
    [value, onChange],
  );

  return (
    <Combobox
      isMulti
      aria-label={ariaLabel}
      value={selectedValues}
      onChange={(next) => handleChange(next as (string | number)[])}
      options={optionsWithAll}
      isLoading={isLoading}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      className={className}
    />
  );
};
